import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import SVG from 'react-inlinesvg';
import 'wicg-inert';
import ReactDOM from 'react-dom';
import { phoneSize } from 'global-styles';

import gsLogo from 'images/SignUp/gs_logo.svg';
import Close from 'images/Dialog/close.svg';
import { GSPLUS_SIGNUP_EN_URL, GSPLUS_SIGNUP_FR_URL } from 'utils/environment';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 9000;
`;

const Overlay = styled.div`
  display: block;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 32, 69, 0.6);
`;

const BodyContainer = styled.div`
  display: block;
  margin-left: 25px;
  margin-right: 25px;
  max-width: 648px;
  width: 100%;
  z-index: 9001;
  box-shadow: 0 0 50px 0 rgba(40, 71, 140, 0.2);
`;

const Top = styled.div`
  position: relative;
  background-color: #ffffff;
  height: 80px;
  padding-top: 24px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  display: flex;
  justify-content: center;

  .close {
    position: absolute;
    cursor: pointer;
    right: 22px;
    top: 22px;
  }
`;

const Body = styled.div`
  background-color: #ffffff;
  max-height: 90vh;
  overflow-y: auto;
  &.bodyOnly {
    border-radius: 12px;
    padding: 0;
    margin: 0;
    @media (max-width: ${phoneSize}) {
      max-height: 80vh;
    }
  }
`;

const Bottom = styled.div`
  background-color: #ffffff;
  height: 20px;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const Center = styled.div`
  text-align: center;
  margin: 0 auto;
  width: 90%;

  h2 {
    margin-bottom: 20px;
  }
`;

const Title = styled.div`
  color: #303333;
  text-align: center;
  font-family: Gilroy;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`;

const Text = styled.div`
  color: #545959;
  text-align: center;
  font-family: Gilroy;
  font-size: 17px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.25px;
`;

const StyledButton = styled.button`
  width: 296px;
  height: 48px;
  border-radius: 999px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${({ outline }) => (outline ? '#d0dbdb' : 'none')};
  background-color: ${({ outline }) => (outline ? 'none' : '#00544E')};
  color: ${({ outline }) => (outline ? '#545959' : '#D9F0FA')};
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  &:hover {
    background-color: ${({ outline }) => (outline ? '#24262608' : '#004645')};
  }
`;

const StyledButtonLabel = styled.span`
  font-family: Gilroy;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.02em;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap-reverse;
  gap: 8px;
`;

const Button = (props) => {
  // eslint-disable-next-line react/prop-types
  const { onClick, disable, label, style, type, input } = props;

  return (
    <>
      <StyledButton
        type={type || 'button'}
        onClick={onClick}
        style={style}
        disable={disable}
        {...input}
        {...props}
      >
        <StyledButtonLabel>{label}</StyledButtonLabel>
      </StyledButton>
    </>
  );
};

class MigrationDialog extends React.PureComponent {
  componentDidMount() {
    if (this.props.open) {
      this.hideNavBar();
    }
  }

  componentDidUpdate(prevProps) {
    const { open } = this.props;

    if (prevProps.open !== open) {
      this.hideNavBar();
      if (open) {
        document.getElementById('app').inert = true;
      } else {
        document.getElementById('app').inert = false;
      }
    }
  }

  hideNavBar = () => {
    const { open } = this.props;
    const navBar = document.getElementById('navBar');

    if (navBar) {
      navBar.classList.toggle('move-down', open);
    }
    document.body.classList.toggle('no-scroll', open);
  };

  render() {
    const {
      open,
      onClose,
      intl: { locale },
    } = this.props;

    if (!open) return <></>;

    return ReactDOM.createPortal(
      <Container>
        <Overlay />
        <BodyContainer role="dialog" aria-modal="true" maxWidth="600px">
          <Top>
            <SVG src={gsLogo} />

            <button
              className="close"
              onClick={onClose}
              aria-label="modal close button"
              aria-describedby="close-modal-button-desc-for-screen-reader"
              type="button"
            >
              <SVG src={Close} />
            </button>
          </Top>
          <Body>
            <Center>
              <Title style={{ marginTop: 20 }}>
                <FormattedMessage defaultMessage="Inkblot is moving to GreenShield+" />
              </Title>
              <Text style={{ marginTop: 8 }}>
                <FormattedMessage defaultMessage="Create an account to access your mental health services on GreenShield+. Sign up with your Inkblot credentials, and we’ll link your accounts automatically." />
              </Text>
            </Center>
            <ButtonContainer style={{ marginTop: 44 }}>
              <Button
                outline
                label={<FormattedMessage defaultMessage="Come back later" />}
                onClick={onClose}
              />
              <Button
                label={
                  <FormattedMessage defaultMessage="Sign up for GreenShield+" />
                }
                onClick={() =>
                  window.location.assign(
                    locale === 'fr'
                      ? GSPLUS_SIGNUP_FR_URL
                      : GSPLUS_SIGNUP_EN_URL,
                  )
                }
              />
            </ButtonContainer>
          </Body>
          <Bottom />
          <p
            style={{ visibility: 'hidden' }}
            id="close-modal-button-desc-for-screen-reader"
          >
            Click this button to keep your session and close the modal
          </p>
        </BodyContainer>
      </Container>,
      document.body,
    );
  }
}

MigrationDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  intl: PropTypes.object,
};

export default injectIntl(MigrationDialog);
